/**
 * Renders the server-authoritative `structures` MapSchema as Three.js objects.
 *
 * The server owns truth: this class only reflects state. It groups placed
 * structures by type and instances each type with `ModelLibrary.instance` (one
 * draw call per type). When a model has not loaded yet it falls back to a
 * cheap procedural stand-in so structures are always visible, and it diffs the
 * incoming set by signature so it only rebuilds on an actual change.
 *
 * The orchestrator constructs this with the shared `ModelLibrary` and calls
 * `sync(Array.from(state.structures.values()))` from the net patch path.
 */
import * as THREE from 'three';
import type { ModelLibrary } from './ModelLibrary';

export type StructureType = 'crate' | 'barrel' | 'chest' | 'cannon';

/**
 * Loose view of one structure as published by the room. `rotationY`/`ownerId`
 * are optional so this still renders before the orchestrator extends the
 * `StructureState` schema (they fall back to 0 / unused).
 */
export interface StructureSnapshot {
  id: string;
  type: string;
  x: number;
  y: number;
  z: number;
  rotationY?: number;
  ownerId?: string;
  health?: number;
}

const SUPPORTED: Readonly<Record<string, StructureType>> = {
  crate: 'crate',
  barrel: 'barrel',
  chest: 'chest',
  cannon: 'cannon',
};

const FALLBACK_COLORS: Readonly<Record<StructureType, number>> = {
  crate: 0x8a5a2b,
  barrel: 0x6b4a2a,
  chest: 0x9a7a3a,
  cannon: 0x2a2a2a,
};

interface Placement {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

export class StructureModels {
  private readonly group = new THREE.Group();
  private readonly modelLibrary: ModelLibrary;
  private lastSignature = '';
  private ownedGeometries: THREE.BufferGeometry[] = [];
  private ownedMaterials: THREE.Material[] = [];

  constructor(scene: THREE.Scene, modelLibrary: ModelLibrary) {
    this.group.name = 'structures';
    scene.add(this.group);
    this.modelLibrary = modelLibrary;
  }

  /**
   * Reflect the authoritative structure set. Rebuilds only when the set (or a
   * pose) changes; safe to call every net patch.
   */
  sync(structures: Iterable<StructureSnapshot>): void {
    const signature = this.signature(structures);
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;
    this.rebuild(structures);
  }

  dispose(): void {
    this.group.removeFromParent();
    this.group.clear();
    // Instanced meshes built by `ModelLibrary.instance` share the cached GLB
    // geometry/material, so only fallback-owned resources are disposed here.
    for (const geometry of this.ownedGeometries) geometry.dispose();
    for (const material of this.ownedMaterials) material.dispose();
    this.ownedGeometries = [];
    this.ownedMaterials = [];
    this.lastSignature = '';
  }

  private signature(structures: Iterable<StructureSnapshot>): string {
    const parts: string[] = [];
    for (const structure of structures) {
      if (!SUPPORTED[structure.type]) continue;
      parts.push(
        `${structure.id}|${structure.type}|${structure.x.toFixed(2)}|` +
          `${structure.y.toFixed(2)}|${structure.z.toFixed(2)}|` +
          `${(structure.rotationY ?? 0).toFixed(3)}`,
      );
    }
    return parts.join(';');
  }

  private rebuild(structures: Iterable<StructureSnapshot>): void {
    while (this.group.children.length > 0) {
      this.group.remove(this.group.children[0]);
    }
    for (const geometry of this.ownedGeometries) geometry.dispose();
    for (const material of this.ownedMaterials) material.dispose();
    this.ownedGeometries = [];
    this.ownedMaterials = [];

    const buckets = new Map<StructureType, Placement[]>();
    for (const structure of structures) {
      const type = SUPPORTED[structure.type];
      if (!type) continue;
      let bucket = buckets.get(type);
      if (!bucket) {
        bucket = [];
        buckets.set(type, bucket);
      }
      bucket.push({
        position: new THREE.Vector3(structure.x, structure.y, structure.z),
        quaternion: new THREE.Quaternion().setFromEuler(
          new THREE.Euler(0, structure.rotationY ?? 0, 0, 'YXZ'),
        ),
      });
    }

    for (const [type, placements] of buckets) {
      const mesh = this.modelLibrary.instance(type, placements, undefined, {
        castShadow: true,
        receiveShadow: true,
      });
      if (mesh) {
        mesh.name = `structures-${type}`;
        this.group.add(mesh);
      } else {
        this.addFallback(type, placements);
      }
    }
  }

  private addFallback(type: StructureType, placements: Placement[]): void {
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshStandardMaterial({
      color: FALLBACK_COLORS[type],
      roughness: 0.85,
      metalness: 0.1,
    });
    this.ownedGeometries.push(geometry);
    this.ownedMaterials.push(material);

    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const matrix = new THREE.Matrix4();
    const scale = new THREE.Vector3(1, 1, 1);
    placements.forEach((placement, index) => {
      matrix.compose(placement.position, placement.quaternion, scale);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.name = `structures-${type}-fallback`;
    this.group.add(mesh);
  }
}
